/* eslint-disable max-len */
import { ErrorMessage, Field, useField, useFormikContext } from 'formik';
import { ReactElement, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MAX_DATE } from '../../../types/Constants';
import { FormDateSelectorPropTypes } from '../../../types/propTypes/FormDateSelectorPropTypes';
import { formatTypedDateInput } from '../../../utilities/dateUtilities';
import { applyClassesForInput } from '../../../utilities/formUtilities';
import FormInputLabel from '../FormInputLabel/FormInputLabel';
import InlineError from '../InlineError/InlineError';

/** Complete typed date only: m/d/yyyy or mm/dd/yyyy (exactly 4-digit year). */
const COMPLETE_TYPED_DATE_PATTERN = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isValidCalendarDateParts = (
  month: number,
  day: number,
  year: number
): boolean => {
  if (year < 1000 || year > 2999) return false;
  if (month < 1 || month > 12) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
};

const toIsoDateString = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const parseStrictDateString = (value: string): Date | null => {
  const dateOnly = value.includes('T') ? value.split('T')[0] : value;

  const isoMatch = dateOnly.match(ISO_DATE_PATTERN);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    if (!isValidCalendarDateParts(month, day, year)) return null;
    return new Date(year, month - 1, day);
  }

  const typedMatch = value.match(COMPLETE_TYPED_DATE_PATTERN);
  if (typedMatch) {
    const month = parseInt(typedMatch[1], 10);
    const day = parseInt(typedMatch[2], 10);
    const year = parseInt(typedMatch[3], 10);
    if (!isValidCalendarDateParts(month, day, year)) return null;
    return new Date(year, month - 1, day);
  }

  // Incomplete values like "3/4" or "3/4/102" must not be treated as valid dates.
  return null;
};

const FormDateSelector = ({
  displayName,
  field: fieldName,
  placeholderText,
  disabled,
  classConfig,
  validationSchema,
  minDate,
  maxDate,
  dateFormat = 'MM/dd/yyyy',
  isClearable,
  onFormValueChange,
}: FormDateSelectorPropTypes): ReactElement => {
  const [field, meta, { setValue, setTouched }] = useField(fieldName);
  const { validateField } = useFormikContext();
  const [rawInputValue, setRawInputValue] = useState<string>('');
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean | undefined>(
    undefined
  );
  const datePickerRef = useRef<DatePicker>(null);
  const instanceIdRef = useRef<string>(fieldName);
  const wasCalendarOpenRef = useRef<boolean>(false);

  const commitFieldValue = (next: string): void => {
    setValue(next);
    onFormValueChange?.(next);
  };

  // Convert Formik value to Date object for DatePicker (strict formats only).
  const getDateFromFieldValue = (): Date | null => {
    if (!field.value || typeof field.value !== 'string') return null;
    return parseStrictDateString(field.value);
  };

  const setFormDetails = (value: Date): void => {
    setTouched(true);
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    if (!isValidCalendarDateParts(month, day, year)) {
      commitFieldValue('');
      setTimeout(() => {
        setRawInputValue('');
        validateField(fieldName);
      }, 0);
      return;
    }
    commitFieldValue(toIsoDateString(year, month, day));

    setTimeout(() => {
      setRawInputValue('');
      validateField(fieldName);
    }, 0);
  };

  const handleChangeRaw = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (instanceIdRef.current !== fieldName) return;
    let inputValue = e.target.value || '';
    setRawInputValue(inputValue);

    if (inputValue && isCalendarOpen !== true) {
      setIsCalendarOpen(true);
      wasCalendarOpenRef.current = true;
    }

    if (!inputValue) {
      commitFieldValue('');
      setTouched(true);
      setTimeout(() => validateField(fieldName), 0);
      return;
    }

    const formatted = formatTypedDateInput(inputValue);
    if (formatted !== inputValue) {
      e.target.value = formatted;
      inputValue = formatted;
      setRawInputValue(formatted);
    }

    const match = inputValue.match(COMPLETE_TYPED_DATE_PATTERN);

    if (match) {
      const month = parseInt(match[1], 10);
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      if (isValidCalendarDateParts(month, day, year)) {
        commitFieldValue(toIsoDateString(year, month, day));
        setTimeout(() => validateField(fieldName), 0);
      } else {
        // if the value is properly format but not valid date, clear it
        e.target.value = '';
        setRawInputValue('');
        commitFieldValue('');
        setTouched(true);
        setTimeout(() => validateField(fieldName), 0);
      }
    } else {
      // Incomplete typed values (e.g. "3/4", "3/4/102") stay as raw text for validation.
      commitFieldValue(inputValue);
      setTimeout(() => validateField(fieldName), 0);
    }
  };


  const handleClickOutside = (): void => {
    if (!wasCalendarOpenRef.current && isCalendarOpen !== true) return;
    if (instanceIdRef.current !== fieldName) return;

    setIsCalendarOpen(false);
    wasCalendarOpenRef.current = false;

    if (!rawInputValue) {
      if (!meta.touched) setTouched(true);
      setTimeout(() => validateField(fieldName), 0);
      return;
    }

    if (!meta.touched) setTouched(true);

    setTimeout(() => validateField(fieldName), 0);
  };

  const handleFocus = (): void => {
    // Always allow calendar to open on focus, even if there's a validation error
    // This helps users select a valid date when they have invalid input
    setIsCalendarOpen(undefined);
  };

  const handleCalendarOpen = (): void => {
    setIsCalendarOpen(true);
    wasCalendarOpenRef.current = true;
  };

  const handleCalendarClose = (): void => {
    if (instanceIdRef.current !== fieldName) return;
    setIsCalendarOpen(false);
    wasCalendarOpenRef.current = false;
  };

  return (
    <div
      className={`field-container date-input ${disabled ? 'disabled' : ''} ${classConfig?.containerClasses || ''
        }`}
      data-testid={`form-date-selector-${fieldName}`}
    >
      <FormInputLabel
        field={fieldName}
        displayName={displayName}
        validationSchema={validationSchema}
        classes={classConfig?.labelClasses || ''}
      />
      <Field
        name={fieldName}
        id={fieldName}
        component={DatePicker}
        label={displayName}
        dateFormat={dateFormat}
        placeholderText={placeholderText ?? 'mm/dd/yyyy'}
        className={applyClassesForInput(
          fieldName,
          `${classConfig?.inputClasses || ''} data-input`
        )}
        disabled={disabled}
        ref={datePickerRef}
        selected={getDateFromFieldValue()}
        value={rawInputValue.length > 0 ? rawInputValue : undefined}
        minDate={minDate ?? null}
        maxDate={maxDate ?? MAX_DATE}
        isClearable={isClearable}
        open={isCalendarOpen}
        onChangeRaw={handleChangeRaw}
        onFocus={handleFocus}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        onChange={(val: Date | null) => {
          if (instanceIdRef.current !== fieldName) return;
          if (val) setFormDetails(val);
          else if (isClearable) {
            setTouched(true);
            commitFieldValue('');
            setTimeout(() => validateField(fieldName), 0);
          }
          setIsCalendarOpen(false);
          wasCalendarOpenRef.current = false;
        }}
        onSelect={(val: Date | null) => {
          if (instanceIdRef.current !== fieldName) return;
          if (val) setFormDetails(val);
          setIsCalendarOpen(false);
          wasCalendarOpenRef.current = false;
        }}
        onClickOutside={handleClickOutside}
      />
      <ErrorMessage name={fieldName} component={InlineError}></ErrorMessage>
    </div>
  );
};

export default FormDateSelector;
